// the date picker — Jalali or Gregorian (T-157: the calendar comes from calendar.ts; the layout, keys and phone sheet are the
// same for both).
import { isoToJdn, jdnToIso } from '../rules.ts';
import { calendarFor, jalali, type Calendar } from '../calendar.ts';
import { closeLayer, icon, narrow, openLayer } from './core.ts';
import { rules } from './validation.ts';

/** A date as a Julian day number (what the picker and rules.ts compute with); null = no date. */
type Jdn = number | null;

interface PickerOptions {
  cal: Calendar;
  value: Jdn;
  noFuture: boolean;
  min: Jdn;
  clear: boolean;
  onPick: (jdn: Jdn) => void;
}

interface PickerState {
  view: 'days' | 'months';
  sel: Jdn;
  y: number;
  m: number;
}

// ---------------- date picker ----------------
/** The picker's own words, in the page's language. */
const WORDS = {
  fa: {
    pick: 'انتخاب تاریخ',
    prevM: 'ماه قبل',
    prevY: 'سال قبل',
    nextM: 'ماه بعد',
    nextY: 'سال بعد',
    today: 'امروز',
    clear: 'پاک کردن',
  },
  en: {
    pick: 'Pick a date',
    prevM: 'Previous month',
    prevY: 'Previous year',
    nextM: 'Next month',
    nextY: 'Next year',
    today: 'Today',
    clear: 'Clear',
  },
};
const lang = (): string => document.documentElement.lang || 'fa';
const words = (): (typeof WORDS)['fa'] => (lang().startsWith('fa') ? WORDS.fa : WORDS.en);
const pad2 = (n: number): string => String(n).padStart(2, '0');
export const now = new Date();
export const TODAY = isoToJdn(now.getFullYear() + '-' + pad2(now.getMonth() + 1) + '-' + pad2(now.getDate()));
/** Kept for pages that format a day as Jalali text (ds.jalaliText). */
export const jalaliText = (jdn: number): string => jalali.format(jdn);
function picker(host: HTMLElement, opt: PickerOptions): { set(jdn: Jdn): void } {
  const cal = opt.cal;
  const w = words();
  const st = { view: 'days', sel: opt.value ?? null } as PickerState;
  const base = cal.fromJdn(opt.value ?? TODAY);
  st.y = base.y;
  st.m = base.m;
  const off = (jdn: number): boolean | number | null => (opt.noFuture && jdn > TODAY) || (opt.min && jdn < opt.min);
  function draw(): void {
    let h =
      `<div class="ds-dp" role="dialog" aria-label="${w.pick}"><div class="ds-dp-head"><button type="button" class="ds-dp-nav" data-nav="-1" aria-label="${st.view === 'days' ? w.prevM : w.prevY}">${icon('chev-next')}</button>` +
      `<button type="button" class="ds-dp-title" data-title>${st.view === 'days' ? cal.months[st.m - 1] + ' ' + st.y : st.y}</button>` +
      `<button type="button" class="ds-dp-nav" data-nav="1" aria-label="${st.view === 'days' ? w.nextM : w.nextY}">${icon('chev-prev')}</button></div>`;
    if (st.view === 'months')
      h +=
        '<div class="ds-dp-months">' +
        cal.months
          .map((mn, i) => `<button type="button" data-month="${i + 1}" aria-current="${i + 1 === st.m}">${mn}</button>`)
          .join('') +
        '</div>';
    else {
      const first = cal.toJdn(st.y, st.m, 1),
        len = cal.monthLength(st.y, st.m);
      // «fri» is the weekend's class (Friday in Jalali; Saturday and Sunday in Gregorian)
      h +=
        '<div class="ds-dp-grid">' +
        cal.weekdaysShort
          .map((d, i) => `<span class="ds-dp-wd${cal.weekend.includes(i) ? ' fri' : ''}">${d}</span>`)
          .join('') +
        '<span></span>'.repeat(cal.weekCol(first));
      for (let d = 1; d <= len; d++) {
        const jdn = first + d - 1,
          c = ['ds-dp-day'];
        if (cal.weekend.includes(cal.weekCol(jdn))) c.push('fri');
        if (jdn === TODAY) c.push('today');
        if (jdn === st.sel) c.push('sel');
        h += `<button type="button" class="${c.join(' ')}" data-d="${jdn}"${off(jdn) ? ' aria-disabled="true"' : ''} aria-label="${cal.longDate(jdn)}"><span>${d}</span></button>`;
      }
      h += '</div>';
    }
    h += `<div class="ds-dp-foot"><button type="button" class="btn btn-ghost btn-sm" data-today>${w.today}</button><span>${st.sel ? cal.longDate(st.sel) : ''}</span>${opt.clear ? `<button type="button" class="btn btn-ghost btn-sm" data-clear>${w.clear}</button>` : ''}</div></div>`;
    host.innerHTML = h;
  }
  host.addEventListener('click', e => {
    const t = (e.target as Element).closest('button');
    if (!t) return;
    e.stopPropagation();
    if (t.dataset.nav) {
      const k = +t.dataset.nav;
      if (st.view === 'months') st.y += k;
      else {
        st.m += k;
        if (st.m < 1) {
          st.m = 12;
          st.y--;
        }
        if (st.m > 12) {
          st.m = 1;
          st.y++;
        }
      }
    } else if (t.dataset.title !== undefined) st.view = st.view === 'days' ? 'months' : 'days';
    else if (t.dataset.month) {
      st.m = +t.dataset.month;
      st.view = 'days';
    } else if (t.dataset.today !== undefined) {
      const today = cal.fromJdn(TODAY);
      st.y = today.y;
      st.m = today.m;
      st.view = 'days';
      if (!off(TODAY)) {
        st.sel = TODAY;
        opt.onPick(TODAY);
      }
    } else if (t.dataset.clear !== undefined) {
      st.sel = null;
      opt.onPick(null);
    } else if (t.dataset.d) {
      if (t.getAttribute('aria-disabled') === 'true') return;
      st.sel = +t.dataset.d;
      opt.onPick(st.sel);
    }
    draw();
  });
  draw();
  return {
    set(jdn) {
      st.sel = jdn;
      if (jdn) {
        const d = cal.fromJdn(jdn);
        st.y = d.y;
        st.m = d.m;
      }
      draw();
    },
  };
}
export function enhanceDate(input: HTMLInputElement): void {
  if (input.dataset.dsReady) return;
  input.dataset.dsReady = '1';
  // T-157: the field's calendar (data-ds-calendar), else the page's (<html data-ds-calendar>), else Jalali
  const cal = calendarFor(input.dataset.dsCalendar ?? document.documentElement.dataset.dsCalendar, lang());
  // the visible text is in the calendar; the hidden input (name) carries ISO yyyy-MM-dd for the server
  // two modes: data-ds-date="hiddenId" (the hidden input carries ISO yyyy-MM-dd) or, for the old data-jdp fields and the grid's
  // date filters, the input itself carries the text (Jalali yyyy/MM/dd, Gregorian yyyy-MM-dd) that the server parses
  const hidden = input.dataset.dsDate
    ? (document.getElementById(input.dataset.dsDate) as HTMLInputElement | null)
    : null;
  const textMode = !hidden;
  const toJdn = (iso: string | undefined): Jdn => (iso ? isoToJdn(iso) : null);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- hidden is null in text mode (textMode is !hidden)
  const current = (): Jdn => (textMode ? cal.parse(input.value) : toJdn(hidden?.value));
  if (hidden?.value) input.value = cal.format(toJdn(hidden.value)!);
  if (textMode) {
    input.autocomplete = 'off';
    if (!input.placeholder) input.placeholder = cal.id === 'jalali' ? '۱۴۰۵/۰۱/۰۱' : '2026-01-01';
    input.classList.add('ds-ltr-num');
  }
  const wrap = document.createElement('div');
  wrap.className = 'ds-dp-pop';
  wrap.hidden = true;
  const set = (jdn: Jdn): void => {
    if (hidden) hidden.value = jdn ? jdnToIso(jdn) : '';
    input.value = jdn ? cal.format(jdn) : '';
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    input.dispatchEvent(new Event('blur'));
  };
  const min = input.dataset.jdpMinDate === 'today' ? TODAY : null;
  const p = picker(wrap, {
    cal,
    value: current(),
    noFuture: input.dataset.noFuture !== undefined,
    min,
    clear: !input.required,
    onPick: jdn => {
      set(jdn);
      close();
      input.focus();
    },
  });
  const place = (): void => {
    if (narrow(640)) return;
    const r = input.getBoundingClientRect();
    wrap.style.top = Math.min(r.bottom + 6, innerHeight - 380) + 'px';
    wrap.style.left = Math.max(8, r.right - 304) + 'px';
  };
  const close = (): void => {
    wrap.hidden = true;
    closeLayer(close);
  };
  const open = (): void => {
    if (input.disabled || (input.readOnly && !textMode)) return;
    openLayer(close);
    document.body.appendChild(wrap);
    wrap.hidden = false;
    place();
    p.set(current());
  };
  const trigger = input.closest('.input-group')?.querySelector('[data-ds-date-open]');
  trigger?.addEventListener('click', e => {
    e.stopPropagation();
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- kept as written (T-081 moved it verbatim)
    wrap.hidden ? open() : close();
  });
  if (textMode && !trigger)
    input.addEventListener('click', () => {
      if (wrap.hidden) open();
    });

  input.addEventListener('keydown', e => {
    if (e.key === 'ArrowDown' && e.altKey) {
      open();
      e.preventDefault();
    }
  });
  input.addEventListener('change', () => {
    if (!hidden) return;
    // a typed day in the field's calendar goes to the hidden ISO input (Jalali through the shared rule, as before)
    if (cal.id === 'jalali') {
      const r = rules?.jalaliDate(input.value);
      if (r && !r.code) hidden.value = r.value!;
    } else {
      const jdn = cal.parse(input.value);
      if (jdn !== null) hidden.value = jdnToIso(jdn);
    }
  });
  document.addEventListener(
    'pointerdown',
    e => {
      if (
        !wrap.hidden &&
        !wrap.contains(e.target as Node) &&
        e.target !== trigger &&
        !trigger?.contains(e.target as Node)
      )
        close();
    },
    true,
  );
}
